import {
	TX_CART_AMOUNT_DUE,
	TX_CART_BALANCE_DUE,
	TX_CART_GRADING_ADJUSTMENT,
	TX_CART_NET_PAID,
	TX_CART_ORDER_TOTAL,
	TX_CART_PAID,
	TX_CART_PAID_IN_STORE_CREDIT,
	TX_CART_STORE_CREDIT,
	TX_CART_STORE_CREDIT_BONUS,
	TX_COUPON,
	TX_REFUNDED,
	TX_SHIPPING,
	TX_SUBTOTAL,
	TX_TAX,
	TX_TOTAL,
} from '@/constants/strings';

import type { BuylistCart, Cart } from '@/models/carts';
import type CheckoutSession from '@/models/checkout/CheckoutSession';
import type { Order } from '@/models/orders';

import {
	getCurrencyMinorCount,
	priceFloatToInt,
} from '@/utils/currency';


// Pure row builders for the totals block. Placed orders read ONLY the stored order fields; live checkout reads the
// cart + checkout session. Deductions are negative amounts.

export type TotalsRowKey =
	| 'amountDue'
	| 'balanceDue'
	| 'coupon'
	| 'gradingAdjustment'
	| 'netPaid'
	| 'paid'
	| 'paidStoreCredit'
	| 'refunded'
	| 'shipping'
	| 'storeCredit'
	| 'storeCreditBonus'
	| 'subtotal'
	| 'tax'
	| 'total';

export type TotalsRowKind = 'line' | 'total';

export interface TotalsRow {
	key: TotalsRowKey;
	label: string;  // TX_ key
	amount: number;
	kind?: TotalsRowKind;
}


// Amounts are differenced in minor units: buylist item prices carry 3 decimals and live tax is fractional, so
// differencing unrounded values can leave a sub-cent row (a "$0.00" adjustment) or rows that don't add up to the total
const toMinor = (amount: number): number => priceFloatToInt(amount);
const fromMinor = (minor: number): number => minor / getCurrencyMinorCount();


export function buildSaleOrderRows(order: Order): TotalsRow[] {

	const rows: TotalsRow[] = [
		{ key: 'subtotal', label: TX_SUBTOTAL, amount: order.totalCart },
	];

	if(order.totalCoupon > 0) {
		rows.push({ key: 'coupon', label: TX_COUPON, amount: -order.totalCoupon });
	}
	if(order.totalShipping > 0) {
		rows.push({ key: 'shipping', label: TX_SHIPPING, amount: order.totalShipping });
	}
	if(order.totalTax > 0) {
		rows.push({ key: 'tax', label: TX_TAX, amount: order.totalTax });
	}

	rows.push({ key: 'total', label: TX_CART_ORDER_TOTAL, amount: order.totalGrand, kind: 'total' });

	// Below the total, money movements as positive amounts; the labels give the direction. Paid is every method
	// together (store credit + card or other); the order's payment details carry the breakdown
	const paidMinor = toMinor(order.totalStoreCredit) + toMinor(order.totalPaid);
	const refundedMinor = toMinor(order.totalRefund);

	if(paidMinor > 0) {
		rows.push({ key: 'paid', label: TX_CART_PAID, amount: fromMinor(paidMinor) });
	}
	if(order.totalPayable > 0) {
		rows.push({ key: 'balanceDue', label: TX_CART_BALANCE_DUE, amount: order.totalPayable });
	}
	if(refundedMinor > 0) {
		rows.push({ key: 'refunded', label: TX_REFUNDED, amount: fromMinor(refundedMinor) });
		rows.push({ key: 'netPaid', label: TX_CART_NET_PAID, amount: fromMinor(paidMinor - refundedMinor), kind: 'total' });
	}
	return rows;
}

export function buildBuylistOrderRows(order: Order): TotalsRow[] {

	const rows: TotalsRow[] = [];

	const cashSubtotal = order.buylistCart?.cashSubtotal ?? null;
	const gradedTotal = order.buylistCart?.gradedTotal ?? null;

	// total_cart is the offer as recorded; for a store-credit order it includes the bonus over the cash value
	const totalCartMinor = toMinor(order.totalCart);
	const bonusMinor = cashSubtotal !== null ? totalCartMinor - toMinor(cashSubtotal) : 0;

	if(bonusMinor > 0) {
		rows.push({ key: 'subtotal', label: TX_SUBTOTAL, amount: fromMinor(totalCartMinor - bonusMinor) });
		rows.push({ key: 'storeCreditBonus', label: TX_CART_STORE_CREDIT_BONUS, amount: fromMinor(bonusMinor) });
	} else {
		rows.push({ key: 'subtotal', label: TX_SUBTOTAL, amount: fromMinor(totalCartMinor) });
	}

	// The graded total equals the offer until items are graded; only grading can make them differ
	const hasGrading = order.gradings.length > 0;
	const adjustmentMinor = (gradedTotal !== null && hasGrading) ? toMinor(gradedTotal) - totalCartMinor : 0;

	if(adjustmentMinor !== 0) {
		rows.push({ key: 'gradingAdjustment', label: TX_CART_GRADING_ADJUSTMENT, amount: fromMinor(adjustmentMinor) });
	}

	rows.push({ key: 'total', label: TX_TOTAL, amount: fromMinor(totalCartMinor + adjustmentMinor), kind: 'total' });

	// The payout lives on the payment record, not on the order's totals
	if(order.paymentRecord) {
		if(order.paymentRecord.totalStoreCredit > 0) {
			rows.push({ key: 'paidStoreCredit', label: TX_CART_PAID_IN_STORE_CREDIT, amount: order.paymentRecord.totalStoreCredit });
		}
		if(order.paymentRecord.totalPaid > 0) {
			rows.push({ key: 'paid', label: TX_CART_PAID, amount: order.paymentRecord.totalPaid });
		}
	}
	return rows;
}

export function buildOrderRows(order: Order): TotalsRow[] {
	return order.isBuylist ? buildBuylistOrderRows(order) : buildSaleOrderRows(order);
}

export function buildSaleCheckoutRows(cart: Cart, checkoutSession: CheckoutSession | null): TotalsRow[] {

	// Same rate the card charge uses
	const taxRate = checkoutSession?.taxRateSales ?? 0;

	// In minor units like the buylist rows: the order total is the charged figure and tax is what the rows above it
	// leave. Rounded on its own, the tax row could miss the order total by a cent (4.60 at 7.5% is 0.345 of tax,
	// but a 4.94 total)
	const subtotalMinor = toMinor(cart.subTotal);
	const couponMinor = toMinor(cart.couponDiscount);
	const shippingMinor = toMinor(checkoutSession?.shippingCost ?? 0);
	const totalMinor = toMinor(cart.getCheckoutGrandTotal(checkoutSession, { taxRate: taxRate }));
	const taxMinor = totalMinor - Math.max(0, subtotalMinor - couponMinor + shippingMinor);

	const rows: TotalsRow[] = [
		{ key: 'subtotal', label: TX_SUBTOTAL, amount: fromMinor(subtotalMinor) },
	];

	if(couponMinor > 0) {
		rows.push({ key: 'coupon', label: TX_COUPON, amount: -fromMinor(couponMinor) });
	}
	if(shippingMinor > 0) {
		rows.push({ key: 'shipping', label: TX_SHIPPING, amount: fromMinor(shippingMinor) });
	}
	if(taxRate > 0 && taxMinor > 0) {
		rows.push({ key: 'tax', label: TX_TAX, amount: fromMinor(taxMinor) });
	}

	rows.push({ key: 'total', label: TX_CART_ORDER_TOTAL, amount: fromMinor(totalMinor), kind: 'total' });

	if(checkoutSession?.isStoreCreditApplied) {
		const storeCreditMinor = toMinor(checkoutSession.appliedStoreCredit);
		rows.push({ key: 'storeCredit', label: TX_CART_STORE_CREDIT, amount: -fromMinor(storeCreditMinor) });
		rows.push({
			key: 'amountDue',
			label: TX_CART_AMOUNT_DUE,
			amount: fromMinor(Math.max(0, totalMinor - storeCreditMinor)),
			kind: 'total',
		});
	}
	return rows;
}

export function buildBuylistCheckoutRows(cart: BuylistCart, storeCreditPricing: boolean): TotalsRow[] {

	// Line prices as the backend records them at placement, not live buy prices
	const cashMinor = toMinor(cart.subTotalItems);

	if(!storeCreditPricing) {
		return [
			{ key: 'total', label: TX_TOTAL, amount: fromMinor(cashMinor), kind: 'total' },
		];
	}

	const totalMinor = toMinor(cart.subTotalItems + cart.getStoreCreditBonus());
	const bonusMinor = totalMinor - cashMinor;

	const rows: TotalsRow[] = [
		{ key: 'subtotal', label: TX_SUBTOTAL, amount: fromMinor(cashMinor) },
	];
	if(bonusMinor > 0) {
		rows.push({ key: 'storeCreditBonus', label: TX_CART_STORE_CREDIT_BONUS, amount: fromMinor(bonusMinor) });
	}
	rows.push({ key: 'total', label: TX_TOTAL, amount: fromMinor(totalMinor), kind: 'total' });
	return rows;
}
